import { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ErrorLogger } from '../../../utils/Logger';

import { StreamChat } from "stream-chat";
import "stream-chat-react/dist/css/index.css";
import {
  Chat,
  Channel,
  ChannelList,
  LoadingIndicator,
  useChatContext,
} from "stream-chat-react";

import './DashboardChat.scss';
import './DashboardChatSidebar.scss';
import { SessionManager } from '../../../services/SessionManager/SessionManager';
import { BuyerAPIHandler } from '../../../services/NetworkClient/BackendAPIs/BuyerAPIHandler';
import { Buyer } from '../../../Models/Buyer';
import { ChatMessageInput } from '../../../chatModule/ChatMessageInput/ChatMessageInput';
import { ChatMessage } from '../../../chatModule/ChatMessage/ChatMessage';
import { ChannelInner } from '../../../chatModule/ChannelInner';
import { GiphyInMessageFlagProvider } from '../../../chatModule/context/GiphyInMessageFlagContext';
import {CustomChannelPreview} from '../../../chatModule/CustomChannelPreview/CustomChannelPreview';
import { CustomDateSeparator } from '../../../chatModule/components/CustomDateSeparator';
import ChatListHeader from './ChatListHeader';
import logoImageGrey from '../../../assets/images/branding/gg_navbar_brand_grey.png';

const sort = { last_updated: -1 } as const;

interface DashboardChatProps {
  initialSelectedChannel?: string;
  onHeaderBackClick?: () => void;
  isDashboardContext?: boolean; // When true, prevents navigation and stays within dashboard
}

// Inner component to control channel selection and prevent auto-selection
const ChannelListController = ({ 
  currentChannelId 
}: { 
  currentChannelId?: string;
}) => {
  const { setActiveChannel } = useChatContext();
  const currentChannelIdRef = useRef(currentChannelId);
  
  // Keep ref in sync with prop
  useEffect(() => {
    currentChannelIdRef.current = currentChannelId;
  }, [currentChannelId]);
  
  useEffect(() => {
    // When no channel should be selected, explicitly clear the active channel
    // This prevents Stream Chat from auto-selecting the first channel
    if (!currentChannelId) {
      let isClearing = true;
      
      const clearChannel = () => {
        // Check the ref to get the current value, not the closure value
        if (isClearing && !currentChannelIdRef.current) {
          setActiveChannel(undefined);
        }
      };
      
      // Immediate clear
      clearChannel();
      
      // Clear after various delays to catch auto-selection at different render stages
      const timers = [
        setTimeout(clearChannel, 0),
        setTimeout(clearChannel, 50),
        setTimeout(clearChannel, 100),
        setTimeout(clearChannel, 150),
        setTimeout(clearChannel, 200),
        setTimeout(clearChannel, 300),
        setTimeout(clearChannel, 500),
        setTimeout(clearChannel, 800),
        setTimeout(clearChannel, 1200),
      ];
      
      // Use requestAnimationFrame for immediate clearing after render
      const rafIds: number[] = [];
      for (let i = 0; i < 15; i++) {
        rafIds.push(requestAnimationFrame(() => {
          setTimeout(clearChannel, i * 50);
        }));
      }
      
      return () => {
        isClearing = false; // Stop clearing when effect cleanup runs
        timers.forEach(timer => clearTimeout(timer));
        rafIds.forEach(id => cancelAnimationFrame(id));
      };
    }
    // When currentChannelId is set, don't interfere - let Stream Chat's customActiveChannel handle it
  }, [currentChannelId, setActiveChannel]);
  
  return null;
};

const DashboardChat = ({initialSelectedChannel, onHeaderBackClick, isDashboardContext = false}: DashboardChatProps) => {
  
  const navigate = useNavigate();
  const location = useLocation();
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const [buyer, setBuyer] = useState<Buyer | undefined>(undefined)
  const [isMobile, setIsMobile] = useState(false);
  const [showChatView, setShowChatView] = useState(false);
  const [currentChannelId, setCurrentChannelId] = useState<string | undefined>(undefined);

  useEffect(() => {
    document.title = "GenieGig | Your Chats";
  }, [])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 960);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Handle initial channel selection - for both mobile and desktop
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const chatId = searchParams.get('chat');
    
    if (chatId && isMobile) {
      setShowChatView(true);
      setCurrentChannelId(chatId);
      document.body.classList.add('chat-active');
    } else if (initialSelectedChannel) {
      // Use initialSelectedChannel if provided (works for both mobile and desktop)
      if (isMobile && !chatId) {
      setShowChatView(true);
        document.body.classList.add('chat-active');
      }
      // Set the channel ID immediately
      setCurrentChannelId(initialSelectedChannel);
    }
  }, [initialSelectedChannel, isMobile, location.search]);

  // Ensure channel is selected when initialSelectedChannel is provided and chat client is ready
  useEffect(() => {
    if (initialSelectedChannel && chatClient && currentChannelId !== initialSelectedChannel) {
      setCurrentChannelId(initialSelectedChannel);
      if (isMobile) {
        setShowChatView(true);
        document.body.classList.add('chat-active');
      }
    }
    // eslint-disable-next-line
  }, [initialSelectedChannel, chatClient, isMobile]);

  // Handle URL query params and browser back button
  useEffect(() => {
    // Skip URL handling in dashboard context - we manage state internally
    if (isDashboardContext) {
      return;
    }
    
    const searchParams = new URLSearchParams(location.search);
    const chatId = searchParams.get('chat');
    
    if (isMobile) {
      if (chatId) {
        // Chat is open via URL
        setShowChatView(true);
        setCurrentChannelId(chatId);
        document.body.classList.add('chat-active');
      } else if (location.pathname === '/my-messages') {
        // URL changed to messages page without chat param (browser back or direct navigation)
        setShowChatView(false);
        document.body.classList.remove('chat-active');
        setCurrentChannelId(undefined);
        // Clear any active channel selection - this prevents messages from being marked as read
      }
    } else {
      // On desktop, only set channel if explicitly provided via URL
      if (chatId) {
        setCurrentChannelId(chatId);
      } else {
        // Clear selection when no chat param
        setCurrentChannelId(undefined);
      }
    }
  }, [location.search, location.pathname, isMobile, isDashboardContext]);

  useEffect(() => {
    const getUserData = async () => {
      try {
        let buyer = SessionManager.shared.buyer || await BuyerAPIHandler.shared.getBuyerByAuthToken()
        SessionManager.shared.buyer = buyer
        setBuyer(buyer)
      } catch (err) {
        ErrorLogger.log(err, 'DashboardChat');
      }
    }

    const initChat = async () => {
      if(buyer && buyer.id && buyer.chatToken) {
        const client = SessionManager.shared.getChatClient();
        setChatClient(client);
      }
    };

    getUserData().then(() => {
      initChat();
    })
    // eslint-disable-next-line
  }, [buyer]);

  useEffect(() => {
    return () => {
      SessionManager.shared.handleEmptyChatChannels(chatClient)
    }
  },[chatClient])

  // Handle initial channel selection and chat-active class
  useEffect(() => {
    if (initialSelectedChannel && isMobile) {
      setShowChatView(true);
      document.body.classList.add('chat-active');
    } else if (!showChatView && isMobile) {
      document.body.classList.remove('chat-active');
    }
  }, [initialSelectedChannel, showChatView, isMobile]);

  // Cleanup: remove chat-active class when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('chat-active');
    };
  }, []);

  const filters = { type: "messaging", members: { $in: [buyer?.id || ""] } };
  if (!chatClient) {
    return <LoadingIndicator />;
  }

  const handleChannelSelect = (channelId: string) => {
    // Set the channel ID first - this will update customActiveChannel prop
    setCurrentChannelId(channelId);
    
    if (isMobile) {
      setShowChatView(true);
      document.body.classList.add('chat-active');
      
      // Only navigate if not in dashboard context
      if (!isDashboardContext) {
      // Push new history entry so browser back button works correctly
      navigate(`/my-messages?chat=${channelId}`, { replace: false });
      }
      // In dashboard context, we stay on the same page and just update state
    }
    // CustomChannelPreview will call setActiveChannel(channel) which sets it in Stream Chat's context
  };

  const handleBackToList = () => {
    setShowChatView(false);
    setCurrentChannelId(undefined);
    document.body.classList.remove('chat-active');
    
    // Only navigate if not in dashboard context
    if (!isDashboardContext) {
    // Navigate back to messages list (remove query param)
    navigate('/my-messages', { replace: true });
    }
    // In dashboard context, we stay on the same page and just update state
  };
  

  return (
    <div className='chatWrapper'>
      <Chat client={chatClient}>
        <div className="chatLayout">
          <div className={`chatSidebar ${isMobile && showChatView ? 'mobile-hidden' : ''}`}>
            <ChatListHeader 
              showBackButton={isMobile && !!onHeaderBackClick} 
              onBackClick={onHeaderBackClick}
            />
            <>
              <ChannelList 
                filters={filters} 
                sort={sort} 
                customActiveChannel={currentChannelId} 
                Preview={(props) => <CustomChannelPreview {...props} onChannelSelect={handleChannelSelect} />}
              />
              <ChannelListController currentChannelId={currentChannelId} />
            </>
          </div>
          
          {/* Only render Channel component when there's an active channel to prevent marking messages as read */}
          {/* On mobile: only render when showChatView is true (chat is open) */}
          {/* On desktop: only render when currentChannelId is set (explicit channel selection) */}
          {((!isMobile && currentChannelId) || (isMobile && showChatView)) ? (
            <div className={`chatMain ${isMobile && !showChatView ? 'mobile-hidden' : ''}`}>
              <Channel
                Input={ChatMessageInput}
                Message={ChatMessage}
                DateSeparator={CustomDateSeparator}
              >
                {/* <Thread /> */}
                <GiphyInMessageFlagProvider>
                  <ChannelInner onBackClick={handleBackToList} isMobile={isMobile} />
                </GiphyInMessageFlagProvider>
              </Channel>
            </div>
          ) : (
            // Empty state for desktop when no channel is selected
            !isMobile && (
              <div className="chatMain chatMain-empty">
                <div className="chat-empty-state">
                  <img src={logoImageGrey} alt="GenieGig" className="chat-empty-logo" />
                </div>
              </div>
            )
          )}
        </div>
      </Chat>
    </div>
  )
}


export default DashboardChat